import type {
  IAccessibilityPropSDKProps,
  IClickPropsSDKActions,
  IElementPropsSDKActions,
} from '@wix/editor-elements-corvid-utils';
import type { CorvidTypes } from '@wix/editor-elements-types/corvid';
import type {
  IComponentPanelProps,
  IComponentPanelWithStylable,
} from '@wix/editor-elements-types/editor';
import type { DefaultCompPlatformProps } from '@wix/editor-elements-types/thunderbolt';
import type { BaseDataItem, LinkProps } from '@wix/editor-elements-definitions';
import type { A11yInteractions } from '@wix/editor-elements-types/a11y';
import type {
  MapperReportClickProps,
  Direction,
  WithInherit,
} from '@wix/editor-elements-common-utils';
import type { PickStateRefValues } from '@wix/editor-elements-integrations';

export type { CollapsibleTextDefinition } from './documentManagement/CollapsibleText.definition';

export interface ExpandControlData {
  readMoreText: string;
  readLessText: string;
  link?: LinkProps;
  linkText: string;
  tag: keyof JSX.IntrinsicElements;
  textDescription: string;
  direction?: WithInherit<Direction>;
  textDirection?: WithInherit<Direction>;
  buttonDirection?: WithInherit<Direction>;
}

export type ButtonAlignment = 'left' | 'center' | 'right';
export type ButtonAlignmentStyle = 'flex-start' | 'center' | 'flex-end';

export type ExpandMode = 'button' | 'link';
export interface ExpandControlProps {
  showExpandControl: boolean;
  expandControlSpacing?: number;
  expandMode: ExpandMode;
  isExpanded: boolean;
  align: 'left' | 'center' | 'right';
  alignButton: ButtonAlignment;
}
export interface ICollapsibleTextData extends BaseDataItem, ExpandControlData {
  text: string;
}

export interface CollapsibleTextDSProps
  extends DefaultCompPlatformProps,
    ExpandControlProps {
  ellipsis: boolean;
  wrappingStyle: 'single-line' | 'multi-line';
  maxLines: number;
  lastUserDefinedWidth: number;
}

export interface ICollapsibleTextMapperProps
  extends CollapsibleTextDSProps,
    Required<MapperReportClickProps>,
    Partial<ExpandControlData>,
    ICollapsibleTextPreviewMapperProps {
  className?: string;
  stylableClassName?: string;
}

export type ICollapsibleTextPreviewMapperProps = {
  partToPreviewStateMap?: {
    button?: string;
  };
};

export type ICollapsibleTextControllerProps = Omit<
  ICollapsibleTextMapperProps,
  keyof MapperReportClickProps
> & {
  reportBiOnLinkClick: () => void;
  setIsExpanded: (isExpanded: boolean) => void;
  reportBiOnExpandClick: (isExpanded: boolean) => void;
};

export type ICollapsibleTextCompProps = ICollapsibleTextData &
  ICollapsibleTextControllerProps &
  IClickPropsSDKActions &
  IElementPropsSDKActions &
  A11yInteractions &
  IAccessibilityPropSDKProps;

export type CollapsibleTextPanelProps = IComponentPanelProps<
  ICollapsibleTextCompProps,
  ICollapsibleTextData
>;

export type CollapsibleTextPanelPropsWithStylable = IComponentPanelWithStylable<
  ICollapsibleTextCompProps,
  ICollapsibleTextData
>;

export type ICollapsibleTextSDK = {
  text?: string;
  ellipsisEnabled: boolean;
  maxLines: number;
  readMoreActionType: ReadMoreActionType;
  readMoreActionData: LinkToContent | ExpandOnCurrentPage;
  expandText: () => void;
  collapseText: () => void;
  removeReadMoreButton: () => void;
  addReadMoreButton: (buttonText?: string) => void;
  disableEllipsis: () => void;
  enableEllipsis: () => void;
};

export type ICollapsibleTextSDKFactory = CorvidTypes.CorvidSDKFactory<
  ICollapsibleTextCompProps,
  ICollapsibleTextSDK
>;

export type LinkToContent = {
  link: string;
  target?: '_blank' | '_self';
  readMoreButtonVisible?: boolean;
  readMoreButtonText?: string;
};

export type ExpandOnCurrentPage = {
  readMoreButtonText?: string;
  readLessButtonText?: string;
  collapsed?: boolean;
};

export enum ReadMoreActionType {
  LinkToContent = 'LinkToContent',
  ExpandOnCurrentPage = 'ExpandOnCurrentPage',
}

export type ICollapsibleTextStateValues = PickStateRefValues<'reportBi'>;
